import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import { CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts"
import {
  AXIS_PROPS,
  CHART_MARGIN,
  ChartCard,
  type ChartConfig,
  ChartFrame,
  ChartTooltip,
  GRID_PROPS,
  LINE_CURSOR,
  MoneyTooltip,
  Y_AXIS_WIDTH,
} from "@/components/charts"
import { Amount } from "@/components/common/amount"
import { useCompactCurrencyFormat } from "@/hooks/use-compact-currency-format"
import { useInvestments } from "@/hooks/use-investments"
import { api } from "@/lib/api"
import { computeFireProjection } from "./-compute"
import { buildFireHeadline } from "./-headline"
import { CalculatorLayout, NumberField, SliderField } from "./-layout"

export const Route = createFileRoute("/calculators/fire")({
  component: FIRECalculator,
})

const CHART_CONFIG = {
  portfolio: { label: "Portfolio", color: "var(--money-credit)" },
} satisfies ChartConfig

function FIRECalculator() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const fmtCompact = useCompactCurrencyFormat(2)
  const { data: investData } = useInvestments()
  const { data: analytics } = useQuery({
    queryKey: ["analytics", 12],
    queryFn: () =>
      api.get<{ trends: Array<{ needs: number; wants: number }> }>("/analytics?months=12"),
  })
  const [prefilled, setPrefilled] = useState(false)
  const [currentAge, setCurrentAge] = useState(30)
  const [monthlyExpenses, setMonthlyExpenses] = useState(80000)
  const [currentSavings, setCurrentSavings] = useState(2000000)
  const [monthlySavings, setMonthlySavings] = useState(50000)
  const [returnRate, setReturnRate] = useState(12)
  const [inflationRate, setInflationRate] = useState(6)
  const [swr, setSwr] = useState(4)

  // Pre-fill from analytics (expenses) — only once when data arrives
  const analyticsApplied = useRef(false)
  useEffect(() => {
    if (analyticsApplied.current || !analytics?.trends?.length) return
    const avg =
      analytics.trends.reduce((s, t) => s + t.needs + t.wants, 0) / analytics.trends.length
    if (avg > 0) {
      analyticsApplied.current = true
      setMonthlyExpenses(Math.round(avg / 1000) * 1000)
      setPrefilled(true)
    }
  }, [analytics])

  // Pre-fill from investments (savings, monthly SIP) — only once when data arrives
  const investApplied = useRef(false)
  useEffect(() => {
    if (investApplied.current || !investData) return
    let applied = false
    if (investData.summary?.totalCurrent) {
      setCurrentSavings(Math.round(investData.summary.totalCurrent / 10000) * 10000)
      applied = true
    }
    const allSipsTotal =
      investData.sips?.all?.reduce((s, sip) => s + parseFloat(sip.amount), 0) ?? 0
    if (allSipsTotal > 0) {
      setMonthlySavings(Math.round(allSipsTotal / 1000) * 1000)
      applied = true
    }
    if (applied) {
      investApplied.current = true
      setPrefilled(true)
    }
  }, [investData])

  // The inflation slider is shown but (as before) not part of the projection.
  const result = useMemo(
    () =>
      computeFireProjection({
        currentAge,
        monthlyExpenses,
        currentSavings,
        monthlySavings,
        returnRate,
        swr,
      }),
    [currentAge, monthlyExpenses, currentSavings, monthlySavings, returnRate, swr],
  )
  const { fireNumber, yearlyData, fireYear, fireAge } = result
  const headline = buildFireHeadline(result)

  // For the chart, only plot until ~5 years past FIRE or max 50
  const chartData = fireYear
    ? yearlyData.slice(0, Math.min(fireYear + 5, yearlyData.length))
    : yearlyData

  return (
    <CalculatorLayout
      title="FIRE Calculator"
      headline={headline}
      isDesktop={isDesktop}
      prefilledNote={prefilled ? "Pre-filled from your data" : null}
      inputs={
        <>
          <NumberField
            id="fire-age"
            label="Current age"
            value={currentAge}
            onChange={setCurrentAge}
            clamp={(n) => Math.max(18, Math.min(70, n))}
          />
          <NumberField
            id="fire-expenses"
            label="Monthly expenses (₹)"
            value={monthlyExpenses}
            onChange={setMonthlyExpenses}
            clamp={(n) => Math.max(5000, n)}
          />
          <NumberField
            id="fire-savings"
            label="Current savings / investments (₹)"
            value={currentSavings}
            onChange={setCurrentSavings}
            clamp={(n) => Math.max(0, n)}
          />
          <NumberField
            id="fire-monthly-savings"
            label="Monthly savings (₹)"
            value={monthlySavings}
            onChange={setMonthlySavings}
            clamp={(n) => Math.max(0, n)}
          />
          <SliderField
            id="fire-return"
            label="Portfolio return"
            value={returnRate}
            onChange={setReturnRate}
            min={6}
            max={18}
            format={(n) => `${n}% p.a.`}
          />
          <SliderField
            id="fire-swr"
            label="Safe withdrawal rate"
            value={swr}
            onChange={setSwr}
            min={2}
            max={6}
            step={0.5}
            format={(n) => `${n}%`}
            minLabel="2% (conservative)"
            maxLabel="6% (aggressive)"
          />
          <SliderField
            id="fire-inflation"
            label="Inflation"
            value={inflationRate}
            onChange={setInflationRate}
            min={3}
            max={10}
            format={(n) => `${n}%`}
          />
        </>
      }
    >
      <StatStrip
        isDesktop={isDesktop}
        items={[
          {
            label: "FIRE number",
            value: <Amount value={Math.round(fireNumber)} />,
            meta:
              monthlyExpenses * 12 * 12 < fireNumber
                ? `${(fireNumber / (monthlyExpenses * 12)).toFixed(0)}× annual expenses`
                : undefined,
          },
          {
            label: "Years to FIRE",
            value: fireYear ? `${fireYear}Y` : ">50Y",
            meta: fireYear ? undefined : "not within 50 years",
            tone: fireYear ? "good" : "warn",
          },
          {
            label: "FIRE age",
            value: fireAge ? String(fireAge) : "—",
            meta: fireAge ? (
              <>
                Monthly income <Amount value={Math.round((fireNumber * swr) / 100 / 12)} />
              </>
            ) : undefined,
          },
        ]}
      />
      <ChartCard title="Portfolio growth vs FIRE target" description="By age">
        <ChartFrame config={CHART_CONFIG}>
          <LineChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="age" {...AXIS_PROPS} minTickGap={16} />
            <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH + 8} tickFormatter={fmtCompact} />
            <ChartTooltip
              content={<MoneyTooltip labelFormatter={(v) => `Age ${v}`} />}
              cursor={LINE_CURSOR}
            />
            <ReferenceLine
              y={fireNumber}
              stroke="var(--text-muted)"
              strokeDasharray="4 4"
              label={{
                value: "FIRE",
                position: "insideTopLeft",
                fontSize: "0.6875rem",
                fill: "var(--text-muted)",
              }}
            />
            <Line
              type="monotone"
              dataKey="portfolio"
              name="Portfolio"
              stroke="var(--color-portfolio)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ChartFrame>
      </ChartCard>
    </CalculatorLayout>
  )
}
